// app/frontend/pages/Login.tsx
import { Head } from '@inertiajs/react';
import { useState } from 'react';
import { useForm } from '@mantine/form';
import {
  Container,
  Title,
  Button,
  TextInput,
  PasswordInput,
  Paper,
  Text,
  Checkbox,
  Stack,
  Anchor,
  Box,
  MantineProvider,
  createTheme,
  MantineTheme,
} from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconMail, IconLock, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useUser } from '../src/context/UserContext';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../styles/Login-form.css';
import logoUrl from '/assets/NEW WCWD LOGO WHITE.svg';

const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: {
    fontFamily: 'Poppins, sans-serif',
  },
  components: {
    TextInput: {
      styles: () => ({
        input: {
          fontSize: 16,
        },
      }),
    },
  },
});

export default function Login() {
  const { refreshUser } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const returnTo = searchParams.get('return_to');

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
      remember_me: false,
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      password: (value) => (value.length < 1 ? 'Password is required' : null),
    },
    validateInputOnBlur: true,
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    try {
      const formData = new FormData();
      formData.append('user[email]', values.email);
      formData.append('user[password]', values.password);
      formData.append('user[remember_me]', values.remember_me.toString());
      if (returnTo) {
        formData.append('return_to', returnTo);
      }

      const response = await fetch('/login', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        showNotification({
          color: 'green',
          title: 'Login Successful',
          message: 'You have been logged in successfully!',
          icon: <IconCheck />,
          autoClose: 6000,
        });
        await refreshUser();
        setTimeout(() => {
          window.location.href = returnTo || '/';
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        showNotification({
          color: 'red',
          title: 'Login Failed',
          message: data.error || 'Invalid email or password',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
        form.setErrors({ email: ' ', password: 'Invalid email or password' });
      }
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'An error occurred during login',
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MantineProvider theme={theme}>
      {/* Render Notifications container */}
      <Notifications position="top-right" />
      <Head title="Login" />
      <Container
        size="xs"
        mt="lg"
        style={{
          minHeight: '75vh',
        }}
      >
        <Box>
          <Paper
            shadow="md"
            p={30}
            radius="md"
            style={{
              backgroundColor: '#75757530',
            }}
            sx={(theme: MantineTheme) => ({
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
              marginTop: '40px',
              [theme.fn.smallerThan('sm')]: {
                padding: '20px',
                marginTop: '20px',
              },
            })}
          >
            <img
              src={logoUrl}
              alt="Logo"
              className="site-nav-logo mb-4"
              style={{ display: 'block', margin: '0 auto' }}
            />
            <Title order={1} ta="center" mb="md" c="white">
              Log in
            </Title>

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput
                  label="Email"
                  placeholder="your@email.com"
                  required
                  leftSection={<IconMail size={16} />}
                  disabled={isLoading}
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                    input: {
                      fontSize: 16,
                    },
                  }}
                  {...form.getInputProps('email')}
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  required
                  leftSection={<IconLock size={16} />}
                  disabled={isLoading}
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                    input: {
                      fontSize: 16,
                    },
                  }}
                  {...form.getInputProps('password')}
                />

                <Checkbox
                  label="Remember me"
                  styles={{
                    label: {
                      fontSize: 16,
                      color: '#fff',
                    },
                  }}
                  className="mt-2"
                  disabled={isLoading}
                  {...form.getInputProps('remember_me', { type: 'checkbox' })}
                />

                <Button type="submit" fullWidth size="lg" loading={isLoading} className="mt-4 login-button">
                  {isLoading ? 'Logging in...' : 'Log in'}
                </Button>
              </Stack>
            </form>

            <Stack mt="md" gap="xs" className="login-links">
              <Anchor href="/password/new" size="md">
                Forgot your password?
              </Anchor>
              <Anchor href="/register" size="md">
                Don't have an account? Sign up
              </Anchor>
              <Anchor href="/confirmation/new" size="md">
                Didn't receive confirmation instructions?
              </Anchor>
            </Stack>

            <Text c="dimmed" size="sm" ta="center" className="mt-4">
              © 2022–2025
            </Text>
          </Paper>
        </Box>
      </Container>
    </MantineProvider>
  );
}
